import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import CountdownTimer from "../components/CountdownTimer";
import RideCard from "../components/RideCard";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { LOGO_URL, formatDatePt } from "../lib/format";
import { Flame } from "lucide-react";
import MapButton from "../components/MapButton";
import PartnersCarousel from "../components/PartnersCarousel";

export default function HomePage() {
  const [nextRide, setNextRide] = useState(null);
  const [upcoming, setUpcoming] = useState([]);
  const [past, setPast] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [nextRes, upRes, pastRes] = await Promise.all([
        api.get("/rides/next"),
        api.get("/rides", { params: { filter: "upcoming" } }),
        api.get("/rides", { params: { filter: "past" } }),
      ]);
      setNextRide(nextRes.data);
      setUpcoming(upRes.data || []);
      setPast(pastRes.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div className="min-h-screen">
      <PartnersCarousel />

      {/* Hero */}
      <section
        className="relative overflow-hidden border-b border-white/10"
        data-testid="home-hero"
      >
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1634015190736-3cecd58f89fe?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "grayscale(20%) brightness(0.6) contrast(1.15)",
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(9,10,15,0.35) 0%, rgba(9,10,15,0.95) 90%)",
          }}
        />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-20">
          <div className="flex items-center gap-3 mb-6">
            <img src={LOGO_URL} alt="logo" className="h-14 sm:h-16 w-auto" />
            <div>
              <div className="label-caps" style={{ color: "#00E5FF" }}>
                Race Team
              </div>
              <h1 className="font-display text-4xl sm:text-6xl text-white uppercase tracking-tight">
                Rolê <span style={{ color: "#00E5FF" }} className="glow-blue">Speed Freaks</span>
              </h1>
            </div>
          </div>

          <p className="max-w-xl text-white/70 font-body text-sm sm:text-base leading-relaxed">
            Capacete pronto. Data marcada. <br />
            <span className="text-white font-semibold">Sem atrasos.</span> Sem desculpas!
          </p>

          {/* Next ride block */}
          <div className="mt-10 sm:mt-14">
            {loading ? (
              <div className="label-caps">Carregando próximo rolê...</div>
            ) : nextRide ? (
              <div data-testid="next-ride-block">
                <div className="flex items-center gap-2 mb-4">
                  <Flame size={16} style={{ color: "#39FF14" }} />
                  <span
                    className="label-caps"
                    style={{ color: "#39FF14", letterSpacing: "0.28em" }}
                  >
                    Próximo Rolê
                  </span>
                </div>
                <Link
                  to={`/ride/${nextRide.id}`}
                  className="block"
                  data-testid="next-ride-link"
                >
                  <h2 className="font-display text-3xl sm:text-5xl text-white uppercase leading-none hover:text-[#00E5FF] transition-colors">
                    {nextRide.name}
                  </h2>
                </Link>
                <div className="flex flex-wrap items-center gap-4 mt-3 text-white/80 text-sm">
                  <div className="flex items-center gap-2">
                    <MapButton address={nextRide.location} size={18} testid="next-ride-map-btn" />
                    <span>{nextRide.location}</span>
                  </div>
                  <div className="label-caps">
                    {formatDatePt(nextRide.ride_datetime)} · {nextRide.arrival_time}
                  </div>
                </div>

                <div className="mt-6">
                  <CountdownTimer isoDate={nextRide.ride_datetime} />
                </div>

                <div className="mt-6">
                  <Link
                    to={`/ride/${nextRide.id}`}
                    className="btn-primary inline-block px-6 py-3 text-sm"
                    data-testid="hero-confirm-btn"
                  >
                    Confirmar Presença
                  </Link>
                </div>
              </div>
            ) : (
              <div
                className="border border-white/10 bg-[#12141D] p-8 max-w-lg"
                data-testid="no-next-ride"
              >
                <div className="label-caps mb-2">Nenhum rolê agendado</div>
                <h3 className="font-display text-3xl text-white uppercase">
                  Aguardando o próximo rolê
                </h3>
                <p className="text-white/60 text-sm mt-3">
                  O admin ainda não marcou o próximo rolê. Volte em breve — a estrada não espera.
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Rides list */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="label-caps">Agenda</div>
            <h2 className="font-display text-3xl sm:text-4xl text-white uppercase">
              Rolês
            </h2>
          </div>
        </div>

        <Tabs defaultValue="upcoming" className="w-full">
          <TabsList
            className="bg-[#12141D] border border-white/10 rounded-sm p-1"
            data-testid="rides-tabs"
          >
            <TabsTrigger
              value="upcoming"
              className="rounded-sm data-[state=active]:bg-[#00E5FF] data-[state=active]:!text-black font-bold uppercase tracking-widest text-xs px-4"
              data-testid="tab-upcoming"
            >
              Próximos ({upcoming.length})
            </TabsTrigger>
            <TabsTrigger
              value="past"
              className="rounded-sm data-[state=active]:bg-white data-[state=active]:!text-black font-bold uppercase tracking-widest text-xs px-4"
              data-testid="tab-past"
            >
              Passados ({past.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="upcoming" className="mt-6">
            {upcoming.length === 0 ? (
              <EmptyState label="Nenhum rolê próximo por enquanto." />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {upcoming.map((r) => (
                  <RideCard key={r.id} ride={r} isPast={false} />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="past" className="mt-6">
            {past.length === 0 ? (
              <EmptyState label="Ainda sem rolês no histórico." />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {past.map((r) => (
                  <RideCard key={r.id} ride={r} isPast={true} />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </section>

      <footer className="border-t border-white/10 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 text-white/50 text-xs flex items-center justify-between">
          <span className="label-caps">Speed Freaks · Race Team</span>
          <span className="label-caps">Grupo Oficial</span>
        </div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 mt-6 text-center text-white/40 text-xs" data-testid="footer-credit">
          Developed by:{" "}
          <a
            href="https://appnobolso.com.br"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#00E5FF] hover:text-[#39FF14] transition-colors"
            data-testid="footer-credit-link"
          >
            appnobolso.com.br
          </a>
        </div>
      </footer>
    </div>
  );
}

function EmptyState({ label }) {
  return (
    <div
      className="border border-dashed border-white/10 bg-[#12141D] py-16 text-center"
      data-testid="empty-rides"
    >
      <div className="label-caps text-white/60">{label}</div>
    </div>
  );
}
